import { ApiError } from "./api-client";
import { getToken } from "./auth";
import { tabIdentity } from "@/lib/live/live-identity";

/**
 * Ouverture, jointure et fermeture des salles live, côté API.
 *
 * Lister les séances publiques et « mes séances » sont partis le 2026-09-14 :
 * le live à l'écran, c'est le récital, et ces gestes-là vivent dans
 * `recitals-api.ts`. Il reste ici ce que le studio fait d'une salle : y entrer,
 * la fermer quand on s'en va — et, depuis le 2026-09-23, en ouvrir une pour
 * jouer à plusieurs (« Inviter ») et la retrouver par son code (« Rejoindre »).
 *
 * Fichier séparé d'`api-client.ts` à dessein : celui-ci porte déjà le
 * catalogue, la bibliothèque, l'authentification et les dépôts, et grossit à
 * chaque fonctionnalité. Le live est un domaine, pas une route de plus.
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

const SESSIONS_PATH = "/api/live/sessions";

/** Forme de salle acceptée. Doit rester alignée sur `LiveRoomId` côté API. */
const ROOM_PATTERN = /^[0-9a-f]{32}$/;

/**
 * Forme de code acceptée **en entrée**.
 *
 * L'API tire ses codes dans un alphabet sans glyphes ambigus (ni `I`, ni `l`,
 * ni `O`, ni `0`, ni `1`), mais on accepte ici plus large qu'on ne tire : la
 * recherche est exacte de toute façon, et refuser `lO0` avant même de demander
 * ferait passer une faute de frappe pour une règle de saisie incompréhensible.
 * La casse compte, elle, et l'écran du code le dit.
 */
const CODE_PATTERN = /^[A-Za-z0-9]{6}$/;

/** Qui voit la salle. Un récital privé n'est listé nulle part. */
export type LiveVisibility = "public" | "private";

/**
 * Le régime de la séance, tel que le service le publie.
 *
 * `all` — tout le monde joue ; `lead` — une personne mène et les autres
 * écoutent ; `idle` — personne n'a encore pris la main.
 */
export type LiveRegime = "all" | "lead" | "idle";

/**
 * Ce que le billet nous fait être dans la salle.
 *
 * `player` — participant d'une séance ordinaire ; `performer` — l'interprète
 * d'un récital ; `listener` — spectateur d'un récital : il voit et entend,
 * son jeton ne lui laisse rien publier, et le studio se verrouille sur ce
 * mot.
 *
 * C'est Symfony qui le décide, une fois, en signant le jeton LiveKit ; LiveKit
 * et le service Go tiennent chacun leur garde sur le même grant. Le front le
 * lit et s'y conforme : son verrou n'est pas une sécurité, c'est ce qui évite
 * d'offrir des gestes qui seraient refusés.
 */
export type LiveRole = "player" | "performer" | "listener";

/**
 * L'enregistrement d'un récital, tel que l'interprète l'a voulu.
 *
 * `off` — pas demandé ; `on` — demandé et pas arrêté ; `stopped` — arrêté par
 * l'interprète pendant le récital. `unavailable` ne se lit que dans le billet de
 * l'interprète : l'enregistrement est voulu, mais l'enregistreur n'a pas
 * accepté de commencer, et la marque « enregistré » ne doit pas mentir.
 * `full` non plus : voulu, mais la place que le forfait réserve aux
 * enregistrements est prise, et l'enregistreur n'a pas été appelé.
 */
export type LiveRecitalRecording = "off" | "on" | "stopped" | "unavailable" | "full";

/** Le récital dont une séance est la salle. */
export interface LiveSessionRecital {
  id: string;
  title: string;
  scheduledAt: string;
  /**
   * Le code du récital, ou `null` : sans public il n'y en a pas, et un récital
   * privé ne le rend qu'à son interprète.
   *
   * **À ne pas confondre avec `LiveSessionSummary.code`**, qui est celui de la
   * *séance* et vaut toujours `null` pour la salle d'un récital. Les deux
   * cohabitent parce qu'ils n'ont pas la même durée de vie : celui d'une séance
   * est rendu à sa fermeture et peut resservir, celui d'un récital reste la
   * trace de ce qui a été donné.
   */
  code: string | null;
  recording: LiveRecitalRecording;
  /** Sans public : le studio dit « enregistrement » plutôt que « live ». */
  recordingOnly: boolean;
  /**
   * Les portes sont-elles ouvertes ? Faux tant que l'interprète est seul sur
   * scène : le studio lui montre alors le geste qui ouvre, et personne d'autre
   * n'obtient de jeton.
   */
  open: boolean;
}

/** Le morceau sur lequel la séance est ouverte. `null` quand il n'y en a pas. */
export interface LiveSessionPiece {
  id: string;
  title: string;
  composer: string | null;
}

/**
 * Ce qu'on sait d'une séance sans y être entré.
 *
 * `code` n'est rendu **qu'au propriétaire** (`mine`), et vaut `null` partout
 * ailleurs : c'est le seul secret de ce type, et il ne doit pas voyager dans
 * une liste publique. `listeners` compte tout le monde sauf celui qui joue.
 */
export interface LiveSessionSummary {
  room: string;
  name: string | null;
  visibility: LiveVisibility;
  code: string | null;
  owner: { nickname: string | null };
  mine: boolean;
  piece: LiveSessionPiece | null;
  listeners: number;
  regime: LiveRegime;
  /** Le nom de qui mène, vide quand personne ne mène ou qu'il est introuvable. */
  leadName: string | null;
  /**
   * Le récital dont cette séance est la salle, `null` pour une séance
   * ordinaire. C'est lui qui fait d'un billet de jointure un billet de
   * spectateur — voir `LiveSessionTicket.role`.
   */
  recital: LiveSessionRecital | null;
  openedAt: string;
  closedAt: string | null;
}

/**
 * Ce que l'API rend pour entrer dans une salle.
 *
 * Il n'y a **pas** d'URL à partager ici : l'API ne connaît pas l'origine sur
 * laquelle le studio est servi, et n'a pas à connaître son plan d'URL. Le lien
 * se compose côté front, cf. `lib/live/live-link.ts`.
 */
export interface LiveSessionTicket {
  room: string;
  identity: string;
  name: string;
  token: string;
  /** URL WebSocket du SFU, telle que ce navigateur doit la joindre. */
  serverUrl: string;
  /**
   * Où mesurer son écart d'horloge avec la séance.
   *
   * Une note ne porte pas un instant mais une position sur une grille
   * partagée ; elle ne veut donc dire quelque chose chez le partenaire que si
   * les deux machines s'accordent sur cette grille. C'est cette URL qui rend
   * l'accord possible — un aller-retour, rien d'autre.
   *
   * Chaîne vide sur un environnement sans service de temps. La séance
   * fonctionne quand même : les notes circulent, seul leur alignement se
   * dégrade. Le studio doit donc savoir s'en passer plutôt que d'échouer.
   */
  clockUrl: string;
  /** Échéance du jeton, en secondes Unix. */
  expiresAt: number;
  /** Ce que ce billet nous fait être dans la salle. Voir `LiveRole`. */
  role: LiveRole;
  /**
   * La séance telle que l'API la connaît, quand elle la connaît.
   *
   * Absente sur une salle qui n'a pas d'enregistrement côté API — un
   * environnement où le live tourne sans persistance, ou une salle plus
   * ancienne que la table. Le studio doit donc savoir s'en passer : c'est ce
   * qui lui donne le code à afficher sous le lien, pas ce qui lui permet de
   * jouer.
   */
  session?: LiveSessionSummary;
}

export function isLiveRoomId(value: string): boolean {
  return ROOM_PATTERN.test(value);
}

export function isLiveCode(value: string): boolean {
  return CODE_PATTERN.test(value);
}

/**
 * « Inviter » : ouvre une séance pour jouer à plusieurs, et rend de quoi y
 * entrer. La séance naît en mode Jam, privée, avec un code qu'on se dicte ;
 * son lien se compose ici (`liveJoinUrl`). Exige un compte.
 */
export async function openLiveSession(): Promise<LiveSessionTicket> {
  return post(SESSIONS_PATH);
}

/**
 * La séance ouverte qui porte ce code, ou une `ApiError` portant la phrase à
 * montrer.
 *
 * Le 404 n'est pas rattrapé ici : « aucune séance n'a ce code » est une
 * réponse, pas une panne, et c'est le widget qui sait quoi en faire — il
 * essaie alors le code d'un récital, que le même champ reçoit.
 */
export async function lookupLiveCode(code: string): Promise<LiveSessionSummary> {
  if (!isLiveCode(code)) {
    throw new ApiError("Un code fait six caractères.", 400);
  }

  const response = await fetch(`${API_BASE_URL}${SESSIONS_PATH}/by-code/${encodeURIComponent(code)}`, {
    method: "GET",
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new ApiError(await describe(response), response.status);
  }

  return (await response.json()) as LiveSessionSummary;
}

/**
 * Rejoindre une salle dont on a reçu le lien.
 *
 * `renew` redemande un billet pour une salle où l'on est déjà : même identité,
 * même rôle, mais le nom du compte tel qu'il est maintenant. L'API ne le compte
 * pas comme une nouvelle entrée. Voir le renouvellement dans le contexte live.
 */
export async function joinLiveSession(
  room: string,
  options: { renew?: boolean } = {},
): Promise<LiveSessionTicket> {
  if (!isLiveRoomId(room)) {
    throw new ApiError("Lien de session live invalide.", 400);
  }

  return post(`${SESSIONS_PATH}/${room}/token`, options.renew === true ? { renew: true } : {});
}

/**
 * Ferme une séance qu'on a ouverte.
 *
 * Fermer, c'est aussi rendre le code : l'API le remet à `NULL`, et le même
 * code peut resservir. C'est pour ça que quitter sa propre séance l'appelle
 * plutôt que de laisser la ligne ouverte jusqu'à ce que le service constate
 * la salle vide — entre les deux, le code continuerait de mener nulle part.
 */
export async function closeLiveSession(room: string): Promise<void> {
  if (!isLiveRoomId(room)) {
    throw new ApiError("Salle live invalide.", 400);
  }

  const response = await fetch(`${API_BASE_URL}${SESSIONS_PATH}/${room}/close`, {
    method: "POST",
    headers: authHeaders(),
  });

  if (!response.ok) {
    throw new ApiError(await describe(response), response.status);
  }
}

async function post(
  path: string,
  extra: Record<string, string | boolean> = {},
): Promise<LiveSessionTicket> {
  // L'identité de l'onglet voyage avec la demande : elle survit à un
  // rechargement, donc LiveKit expulse la connexion fantôme au lieu de la
  // laisser traîner dans la salle. Voir `tabIdentity`.
  const identity = tabIdentity();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(identity === null ? extra : { ...extra, identity }),
  });

  if (!response.ok) {
    throw new ApiError(await describe(response), response.status);
  }

  const ticket = (await response.json()) as Omit<LiveSessionTicket, "role"> & { role?: LiveRole };

  // Un billet sans rôle vient d'une API qui ne le signait pas encore : elle
  // n'émettait alors que des grants de joueur, et c'est ce qu'il est. Le
  // laisser indéfini n'en ferait ni un joueur ni un spectateur — et ce qui
  // se réserve aux joueurs, comme le calque du téléphone, se cacherait à tort.
  return { ...ticket, role: ticket.role ?? "player" };
}

/**
 * Le jeton de compte est joint quand il existe, sans être exigé : sur les
 * routes qui l'exigent c'est l'API qui répond 401 — pas à ce module de
 * deviner l'état de la session.
 */
function authHeaders(): Record<string, string> {
  const token = getToken();

  if (!token) {
    return {};
  }

  return { Authorization: `Bearer ${token}` };
}

async function describe(response: Response): Promise<string> {
  if (response.status === 503) {
    return "Le live n'est pas disponible sur cet environnement.";
  }

  if (response.status === 429) {
    return "Trop de demandes coup sur coup. Réessaie dans un instant.";
  }

  try {
    const body = (await response.json()) as { error?: string };

    if (typeof body.error === "string" && body.error !== "") {
      return body.error;
    }
  } catch {
    // Corps illisible : le message générique ci-dessous fera l'affaire.
  }

  return "Le live n'a pas répondu. Réessaie dans un instant.";
}
